import { fullNightPrintCss, openFullNightPrint } from "./fullNightPrint";
import { batterySamples, profileValueText } from "./presentationSeries";
import { presentationProfile, batteryCategory, BATTERY_LABELS, BATTERY_COLOURS, QUADRANTS } from "./presentationProfiles";
import { positionSamples, positionValue } from "./positionSeries";
import { PresentationPlot, quadrantSamples, POSITION_LEGEND } from "./PresentationPlot";
import { displayInventory, orderedInventory, profileOrderKey, channelVisible, channelName, finiteVisibleRange, pooledVisibleRange, amplitudeBounds, TRACE_PALETTE, type DisplayChannel, type DetailWindowKey } from "./channelDisplay";
import { ViewerIcon } from "./ViewerIcons";
import { ViewerDialog } from "./ViewerDialog";
import { ChannelSettings } from "./ChannelSettings";
import { ChannelOrderDialog } from "./ChannelOrderDialog";
import { SleepStagePlot } from "./SleepStagePlot";
import type { ViewerPreferences } from "./preferences";
import { channelValueAt } from "./valueReadout";
import { SLEEP_STAGE_COLORS } from "./stagePalette";
import { ViewerSettings } from "./ViewerSettings";
import { useViewerPreferences } from "./useViewerPreferences";
import { usePositionDistribution } from "./usePositionDistribution";
import {
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ModalityCapabilityV1,
  QuaternionSignalModalityV1,
  RecordingCapabilitiesV1,
  SignalCategoricalSeriesV1,
  SignalMinMaxSeriesV1,
  SignalNativeSeriesV1,
  SignalOverviewSeriesV1,
  SignalOverviewV1,
  SignalProfileV1,
  SignalWindowSeriesV1,
  SignalWindowV1,
} from "@rekonas/domain";
import { Alert, Spinner } from "@rekonas/ui";
import {
  createDefaultCameraViews,
  linkCameraViews,
  updateCameraViews,
  type CameraView,
} from "./camera";
import { ORIENTATION_MODELS, orientationModelForModality } from "./config";
import {
  parseJumpTime,
  applyViewerArrowKey,
  clampSelectedTime,
  detailWindowStart,
  hasNativeArrowKeyBehavior,
  INITIAL_TIME_WINDOW,
  isActiveTimelineDrag,
  timelineSecondsAtClientX,
  type TimeWindow,
} from "./interaction";
import { CombinedOrientation } from "./CombinedOrientation";
import { DEFAULT_COMBINED_CAMERA } from "./combinedOrientation";
import { OrientationPanel } from "./OrientationPanel";
import { orientationAtTime, type OrientationSelection } from "./orientation";
import { PlaybackController, PLAYBACK_RATES, PLAYBACK_TILE_SECONDS, type PlaybackSnapshot, type PlaybackRate } from "./playback";

export const MAX_SIGNAL_WINDOWS = 5;
const EMPTY_PLAYBACK: PlaybackSnapshot = {
  status: "paused", positionSeconds: 0, rate: 1, bufferedUntilSeconds: 0,
  generation: 0, requiresResume: false, error: null,
};
const emptyPlaybackSnapshot = () => EMPTY_PLAYBACK;
const emptyPlaybackSubscribe = () => () => {};
type Layer = "both" | "recorded" | "derived";
type PlotSeries = SignalOverviewSeriesV1 | SignalWindowSeriesV1;

export interface ViewerDataSource {
  /** Retained for old route adapters; the viewer never invokes the raw scanner. */
  loadPositionDistribution?(signal: AbortSignal): Promise<import("@rekonas/domain").PositionDistributionV1>;
  loadMetricsPositionReadiness?(signal: AbortSignal): Promise<import("@rekonas/domain").MetricsPositionReadiness>;
  loadMetricsPositionDistribution?(identity: import("@rekonas/domain").MetricsPositionIdentity, signal: AbortSignal): Promise<import("@rekonas/domain").MetricsPositionDistribution>;
  /** Export-only snapshot adapter for mutable scientific overlays. */
  revalidateExport?(signal: AbortSignal): Promise<void>;
  loadExportOverview?(modalities: string[], maxPoints: number, signal: AbortSignal): Promise<SignalOverviewV1>;
  key: readonly unknown[];
  recordingRef: string;
  durationSeconds: number;
  profile: SignalProfileV1;
  loadCapabilities(signal: AbortSignal): Promise<RecordingCapabilitiesV1>;
  loadOverview(modalities: string[], maxPoints: number, signal: AbortSignal): Promise<SignalOverviewV1>;
  loadWindow(
    modalities: string[],
    startSeconds: number,
    durationSeconds: 15 | 30 | 60,
    signal: AbortSignal,
  ): Promise<SignalWindowV1>;
}

export interface HypnogramViewerState {
  selectedSeconds: number;
  displayedSeconds: number;
  onPreview: (seconds: number | null) => void;
  visibleStartSeconds: number;
  visibleEndSeconds: number;
  fullNight: boolean;
  onSeek: (seconds: number) => void;
}

export interface ViewerStatusInfo {
  sourceKey: string;
  recordingRef: string;
  dataVersion: string | null;
  availableCount: number;
  unavailableCount: number;
  capabilitiesLoaded: boolean;
  playbackActive: boolean;
}

export function isCurrentSignalWindow(
  result: SignalWindowV1,
  expected: {
    recordingRef: string;
    dataVersion: string;
    startSeconds: number;
    durationSeconds: 15 | 30 | 60;
  },
): boolean {
  return result.recording_ref === expected.recordingRef &&
    result.data_version === expected.dataVersion &&
    result.requested.start_seconds === expected.startSeconds &&
    result.requested.duration_seconds === expected.durationSeconds;
}

export function trimSignalWindowCache(
  client: ReturnType<typeof useQueryClient>,
  scope: readonly unknown[],
  maximum = MAX_SIGNAL_WINDOWS,
): void {
  const windows = client.getQueryCache().findAll({ queryKey: [...scope, "window"] })
    .sort((left, right) => right.getObserversCount() - left.getObserversCount() || right.state.dataUpdatedAt - left.state.dataUpdatedAt);
  windows.slice(maximum).forEach((query) => client.removeQueries({ queryKey: query.queryKey, exact: true }));
}

export function formatRecordingTime(seconds: number, includeSeconds = false): string {
  const safe = Math.max(0, Math.round(Number.isFinite(seconds) ? seconds : 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remainder = safe % 60;
  return includeSeconds
    ? `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
    : `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function separatedChannelLabel(modalityLabel: string, channelLabel: string, index: number): string {
  const [context, modalityName] = modalityLabel.split("·").map((part) => part.trim());
  const humanChannel = channelLabel
    .replace(/^[a-z]+\d+[_.\s-]+/i, "")
    .replaceAll(/[_-]+/g, " ")
    .replace(/([a-z])([0-9])/gi, "$1 $2")
    .replaceAll(/\s+/g, " ")
    .trim();
  const base = modalityName || context;
  const distinctChannel = humanChannel.localeCompare(base, undefined, { sensitivity: "accent" }) === 0
    ? `${humanChannel} ${index + 1}`
    : humanChannel;
  return modalityName ? `${context} · ${distinctChannel}` : distinctChannel;
}

function capabilityMessage(capability: ModalityCapabilityV1 | undefined, modalityId: string): string {
  if (modalityId === "swa") return "Not computed";
  if (modalityId === "position") return "Unavailable";
  if (!capability) return "Capability unavailable";
  if (capability.state === "not_recorded") return "Not recorded";
  if (capability.state === "not_computed") return "Not computed";
  if (capability.state === "unsupported_mapping") return "Unsupported mapping";
  if (capability.state === "temporarily_unavailable") return "Temporarily unavailable";
  if (capability.state === "queued" || capability.state === "processing") return "Processing";
  if (capability.state === "failed") return modalityId === "sleep_stage" ? "Sleep analysis failed" : "Unavailable";
  return "No samples in this interval";
}

function SignalCanvas(props: {
  interval: { start_seconds: number; end_seconds: number };
  series: Array<SignalNativeSeriesV1 | SignalMinMaxSeriesV1>;
  onSeek(seconds: number): void;
  channels: DisplayChannel[]; preferences:ViewerPreferences; centres:Record<string,number>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const draw = () => {
      const bounds = canvas.getBoundingClientRect();
      const width = Math.max(1, Math.floor(bounds.width || 900));
      const height = Math.max(56, Math.floor(bounds.height || 68));
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      const context = canvas.getContext("2d");
      if (!context) return;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);
      context.strokeStyle = "rgba(14, 36, 70, 0.10)";
      context.lineWidth = 1;
      for (let index = 1; index < 6; index += 1) {
        const x = (width * index) / 6;
        context.beginPath(); context.moveTo(x, 0); context.lineTo(x, height); context.stroke();
      }
      const pooled=pooledVisibleRange(props.series,props.interval);
      const xAt = (seconds: number) => ((seconds - props.interval.start_seconds) /
        (props.interval.end_seconds - props.interval.start_seconds)) * width;
      props.series.forEach((item, seriesIndex) => {
        const channel=props.channels.find(c=>c.channelId===item.channel_id);
        const override=channel?props.preferences.channelDisplay[channel.key]||{}:{};
        const profile=channel?presentationProfile(channel):null;
        const defaults=profile?.scale?{minimum:profile.scale[0],maximum:profile.scale[1]}:profile?.id === "light"?{minimum:0,maximum:Math.max(1,finiteVisibleRange(item,props.interval)?.maximum||1)}:null;
        const bounds=override.scaleMode === "fixed"?amplitudeBounds(finiteVisibleRange(item,props.interval),override,channel?props.centres[channel.key]:undefined):defaults||amplitudeBounds(pooled,override);
        const span=Math.max(bounds.maximum-bounds.minimum,1e-12);
        const yAt=(value:number)=>height-9-((value-bounds.minimum)/span)*(height-18);
        const paletteIndex=channel?props.channels.indexOf(channel):seriesIndex;
        context.strokeStyle = override.colour || (profile?.id === "light"?"#ffd400":null) || channel?.defaultColour || TRACE_PALETTE[paletteIndex % TRACE_PALETTE.length];
        context.lineWidth = seriesIndex === 0 ? 1.3 : 1;
        if (item.representation === "minmax") {
          item.bins.forEach((bin) => {
            if (bin.minimum == null || bin.maximum == null || profile?.id === "battery" && (batteryCategory(bin.minimum)===null || batteryCategory(bin.maximum)===null) || profile?.id === "light" && bin.minimum<0) return;
            const x = xAt((bin.start_seconds + bin.end_seconds) / 2);
            context.beginPath(); context.moveTo(x, yAt(bin.minimum)); context.lineTo(x, yAt(bin.maximum)); context.stroke();
            if(profile?.id === "light"){
              context.fillStyle=override.colour||"#ffd400";context.globalAlpha=.22;
              const edges=[bin.start_seconds,bin.end_seconds,...(item.gaps||[]).flatMap(g=>[g.start_seconds,g.end_seconds]).filter(t=>t>bin.start_seconds&&t<bin.end_seconds)].sort((a,b)=>a-b);
              edges.slice(0,-1).forEach((start,i)=>{const end=edges[i+1];if(!item.gaps?.some(g=>g.start_seconds<end&&g.end_seconds>start))context.fillRect(xAt(start),yAt(bin.maximum!),Math.max(1,xAt(end)-xAt(start)),height-9-yAt(bin.maximum!));});context.globalAlpha=1;
            }
          });
          return;
        }
        context.beginPath();
        let drawing = false;
        item.values.forEach((value, index) => {
          if (value == null || !Number.isFinite(value) || profile?.id === "battery" && batteryCategory(value)===null || profile?.id === "light" && value<0) { drawing = false; return; }
          const x = xAt(item.start_seconds + index * item.sample_period_seconds);
          const y = yAt(value);
          if (!drawing) context.moveTo(x, y); else context.lineTo(x, y);
          drawing = true;
        });
        context.globalAlpha=profile?.id === "light"?.9:1;context.stroke();context.globalAlpha=1;
        if(profile?.id === "light") {
          const colour=override.colour||"#ffd400";
          context.fillStyle=colour;context.globalAlpha=.22;
          let segment:Array<[number,number]>=[];
          const fill=()=>{if(segment.length){context.beginPath();context.moveTo(segment[0][0],height-9);segment.forEach(([x,y])=>context.lineTo(x,y));context.lineTo(segment[segment.length-1][0],height-9);context.closePath();context.fill();}segment=[];};
          item.values.forEach((v,i)=>{if(v===null||!Number.isFinite(v)||v<0){fill();return;}segment.push([xAt(item.start_seconds+i*item.sample_period_seconds),yAt(v)]);});fill();context.globalAlpha=1;
        }
      });
    };
    draw();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(draw);
    observer?.observe(canvas);
    return () => observer?.disconnect();
  }, [props.interval, props.series,props.channels,props.preferences.channelDisplay,props.centres]);

  function seek(event: MouseEvent<HTMLCanvasElement>) {
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
    props.onSeek(props.interval.start_seconds + ratio * (props.interval.end_seconds - props.interval.start_seconds));
  }
  return <canvas aria-label="Signal plot; click to seek" className="signal-row-canvas" onClick={seek} ref={canvasRef} role="img" />;
}

export function sleepStageClass(label: string): string {
  const normalized = label.trim().toLowerCase().replaceAll(/[^a-z0-9]+/g, "-");
  return ["wake", "n1", "n2", "n3", "rem"].includes(normalized) ? `sleep-${normalized}` : "sleep-unknown";
}

export function isActiveSleepStage(
  highlight: boolean,
  modalityId: string,
  selectedSeconds: number,
  interval: { start_seconds: number; end_seconds: number },
): boolean {
  return highlight && modalityId === "sleep_stage" && selectedSeconds >= interval.start_seconds && selectedSeconds < interval.end_seconds;
}

function CategoricalTrack(props: {
  highlightSelected: boolean;
  interval: { start_seconds: number; end_seconds: number };
  selectedSeconds: number;
  series: SignalCategoricalSeriesV1;
  onSeek(seconds: number): void;
}) {
  const duration = props.interval.end_seconds - props.interval.start_seconds;
  return (
    <button
      aria-label={`${props.series.modality_label} intervals; click to seek`}
      className="categorical-track"
      onClick={(event) => {
        const bounds = event.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
        props.onSeek(props.interval.start_seconds + ratio * duration);
      }}
      type="button"
    >
      {props.series.intervals.map((item) => {
        const left = ((item.start_seconds - props.interval.start_seconds) / duration) * 100;
        const width = ((item.end_seconds - item.start_seconds) / duration) * 100;
        const label = props.series.labels[item.code] || item.code;
        const selected = isActiveSleepStage(props.highlightSelected, props.series.modality_id, props.selectedSeconds, item);
        return (
          <span
            className={`category-segment ${sleepStageClass(label)}${selected ? " category-selected" : ""}`}
            key={`${item.start_seconds}-${item.code}`}
            style={{ left: `${left}%`, width: `${width}%`, backgroundColor: SLEEP_STAGE_COLORS[label] }}
            title={`${label}: ${formatRecordingTime(item.start_seconds, true)}`}
          >
            {width > 5 ? label : ""}
          </span>
        );
      })}
    </button>
  );
}

const TimeAxis = memo(function TimeAxis({ interval }: { interval: { start_seconds: number; end_seconds: number } }) {
  return <div className="signal-time-axis">{Array.from({ length: 7 }, (_, index) => {
    const seconds = interval.start_seconds + ((interval.end_seconds - interval.start_seconds) * index) / 6;
    return <span key={index}>{formatRecordingTime(seconds, interval.end_seconds - interval.start_seconds <= 60)}</span>;
  })}</div>;
});

const TrackRow = memo(function TrackRow(props: {
  capability: ModalityCapabilityV1 | undefined;
  highlightActiveStage: boolean;
  interval: { start_seconds: number; end_seconds: number };
  loading: boolean;
  gap: boolean;
  label: string;
  channels:DisplayChannel[];preferences:ViewerPreferences;setPreferences:Dispatch<SetStateAction<ViewerPreferences>>;
  centres:Record<string,number>;onRecenter(channel:DisplayChannel,reset?:boolean):void;
  modality: SignalProfileV1["modalities"][number];
  requestError: boolean;
  suspended: boolean;
  selectedSeconds: number;
  series: PlotSeries[];
  positionData?: ReturnType<typeof positionSamples>;
  positionReadout?: string;
  fullNight?: boolean;
  onSeek(seconds: number): void;
}) {
  const [settingsOpen,setSettingsOpen]=useState(false);
  const profile=presentationProfile(props.channels[0]||{modalityId:props.modality.id});
  const display=props.preferences.channelDisplay[props.channels[0]?.key]||{};
  const representation=display.positionRepresentation||"quadrants";
  const style=display.presentationStyle||profile.defaultStyle;
  const quadrants=useMemo(()=>quadrantSamples(props.positionData||[]),[props.positionData]);
  const numerical = useMemo(()=>props.series.filter(
    (item): item is SignalNativeSeriesV1 | SignalMinMaxSeriesV1 => item.representation !== "intervals",
  ),[props.series]);
  const batteryData=useMemo(()=>profile.id === "battery" && display.batteryRepresentation === "categories"?batterySamples(numerical):[],[numerical,profile.id,display.batteryRepresentation]);
  const automaticRange=pooledVisibleRange(numerical,props.interval);
  const automaticBounds=automaticRange?amplitudeBounds(automaticRange,{}):null;
  const categorical = props.series.find(
    (item): item is SignalCategoricalSeriesV1 => item.representation === "intervals",
  );
  const status = props.requestError ? "Request error"
    : props.suspended ? "Paused · awaiting access revalidation"
    : props.loading && props.capability?.state === "available" ? "Loading track…"
      : props.gap && props.capability?.state === "available" ? "Gap or no samples in this interval"
      : capabilityMessage(props.capability, props.modality.id);
  return (
    <div className={`signal-track-row state-${props.capability?.state || "unknown"}`}>
      <div className="signal-track-label">
        <div className="channel-label-heading"><strong>{props.label}</strong></div>
        {profile.id === "position" && <output className="channel-value" aria-label={`${props.label} selected position`}>{props.positionReadout||"Missing"}</output>}
        {numerical.map((series) => {
          const value=channelValueAt(series,props.selectedSeconds);
          if(props.preferences.labelWidthMode === "compact" && value.text === "Not available" && profile.id !== "battery")return null;
          return <span className="channel-value" key={series.channel_id} title={value.description}>
            {numerical.length > 1 && <b style={{borderLeft:`4px solid ${props.preferences.channelDisplay[props.channels.find(c=>c.channelId===series.channel_id)?.key||""]?.colour||props.channels.find(c=>c.channelId===series.channel_id)?.defaultColour||TRACE_PALETTE[0]}`,paddingLeft:3}}>{props.channels.find(c=>c.channelId===series.channel_id)?channelName(props.channels.find(c=>c.channelId===series.channel_id)!,props.preferences):series.channel_label}: </b>}{value.text === "Not available" && profile.id !== "battery" ? "No value at cursor" : profileValueText(series,props.selectedSeconds,profile,display)}
          </span>;
        })}
        {numerical.length>1&&<small>Automatic traces share the group fit; fixed spans are per trace. Settings show each effective scale.</small>}
        <button className="channel-cog icon-button" type="button" data-tooltip={`Settings for ${props.label}`} aria-label={`Settings for ${props.label}`} title={`Settings for ${props.label}`} aria-haspopup="dialog" aria-expanded={settingsOpen} onClick={()=>setSettingsOpen(true)}><ViewerIcon name="settings"/></button>
      </div>
      <div className="signal-track-plot">
        {profile.id !== "position" && numerical.length>0&&!numerical.some(s=>finiteVisibleRange(s,props.interval))&&<span className="plot-no-data">Gap / no finite samples</span>}
        {categorical&&!categorical.intervals.length&&<span className="plot-no-data">No stage intervals</span>}
        {profile.id === "position" ? props.fullNight?<div className="track-status"><span>Full-night position unavailable · choose 15, 30 or 60 seconds</span></div>:props.positionData?.length?<div className="position-lanes">
          {representation !== "angle" && <PresentationPlot compact={representation === "both"} showLegend={representation !== "both"} samples={quadrants} interval={props.interval} style={style} {...POSITION_LEGEND} onSeek={props.onSeek} label="Quadrants · categorical levels"/>}
          {representation !== "quadrants" && <PresentationPlot compact={representation === "both"} samples={props.positionData} interval={props.interval} style={style} {...POSITION_LEGEND} angle onSeek={props.onSeek} label="Angle · 0–360°"/>}
        </div>:<div className="track-status"><span>{props.positionReadout||status}</span></div>
        : profile.id === "battery" && display.batteryRepresentation === "categories" ? <PresentationPlot samples={batteryData} interval={props.interval} style={style} categories={BATTERY_LABELS} colours={BATTERY_COLOURS} reverseCategories onSeek={props.onSeek} label="Battery categories"/>
        : categorical && props.modality.id === "sleep_stage" && props.preferences.channelDisplay[props.channels[0]?.key]?.stageStyle === "hypnogram" ? <SleepStagePlot interval={props.interval} onSeek={props.onSeek} series={categorical}/> : categorical ? <CategoricalTrack highlightSelected={props.highlightActiveStage} interval={props.interval} onSeek={props.onSeek} selectedSeconds={props.selectedSeconds} series={categorical} />
          : numerical.length ? <SignalCanvas interval={props.interval} onSeek={props.onSeek} series={numerical} channels={props.channels} preferences={props.preferences} centres={props.centres} />
            : <div className="track-status"><span>{status}</span></div>}
      </div>
      {settingsOpen&&<ViewerDialog title={`Settings for ${props.label}`} onClose={()=>setSettingsOpen(false)}>
        {props.channels.map((channel,index)=><ChannelSettings key={channel.key} channel={channel} preferences={props.preferences} setPreferences={props.setPreferences} defaultColour={channel.defaultColour||TRACE_PALETTE[index%TRACE_PALETTE.length]} centre={props.centres[channel.key]} automaticBounds={automaticBounds} groupAutomatic={numerical.length>1}
          range={(()=>{const source=numerical.find(s=>s.channel_id===channel.channelId);return source?finiteVisibleRange(source,props.interval):null;})()}
          onRecenter={channel.numeric?()=>props.onRecenter(channel):undefined} onResetCentre={()=>props.onRecenter(channel,true)}/>)}
      </ViewerDialog>}
    </div>
  );
});

function FullNightTimeline(props: {
  duration: number;
  selected: number;
  detailStart: number;
  detailDuration: number | null;
  onCommit(seconds: number): void;
  onPreview(seconds: number | null): void;
}) {
  const dragging = useRef<number | null>(null);
  const timelineDuration = Math.max(props.duration, 1e-9);

  function secondsAt(event: ReactPointerEvent<HTMLDivElement>) {
    const bounds = event.currentTarget.getBoundingClientRect();
    return timelineSecondsAtClientX(event.clientX, bounds.left, bounds.width, props.duration);
  }

  return (
    <div
      aria-label="Full-night timeline scrubber"
      aria-valuemax={props.duration}
      aria-valuemin={0}
      aria-valuenow={props.selected}
      className="full-night-timeline"
      onLostPointerCapture={(event) => {
        if (dragging.current === event.pointerId) {
          dragging.current = null;
          props.onPreview(null);
        }
      }}
      onPointerCancel={(event) => {
        if (dragging.current === event.pointerId) {
          dragging.current = null;
          props.onPreview(null);
        }
      }}
      onPointerDown={(event) => {
        dragging.current = event.pointerId;
        event.currentTarget.setPointerCapture(event.pointerId);
        props.onPreview(secondsAt(event));
      }}
      onPointerMove={(event) => {
        if (isActiveTimelineDrag(dragging.current, event.pointerId, event.currentTarget.hasPointerCapture(event.pointerId))) {
          props.onPreview(secondsAt(event));
        }
      }}
      onPointerUp={(event) => {
        if (dragging.current !== event.pointerId) return;
        const seconds = secondsAt(event);
        dragging.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        props.onCommit(seconds);
      }}
      role="slider"
      tabIndex={0}
    >
      {props.detailDuration != null && <i className="detail-window-range" style={{
        left: `${(props.detailStart / timelineDuration) * 100}%`,
        width: `${Math.max(0.2, (props.detailDuration / timelineDuration) * 100)}%`,
      }} />}
      <i className="full-night-cursor" data-testid="full-night-cursor" style={{ left: `${(props.selected / timelineDuration) * 100}%` }} />
    </div>
  );
}

export function SignalViewer(props: {
  dataSource: ViewerDataSource;
  preferenceIdentity: string;
  summary?: ReactNode;
  provenance?: ReactNode;
  toolbar?: ReactNode;
  qualityLabel?: string;
  recordingLabel?: string;
  onStatusInfoChange?: (info: ViewerStatusInfo) => void;
  attention?: string | null;
  viewPreferences?: ReturnType<typeof useViewerPreferences>;
  pageCards?: boolean;
  renderHypnogram?: (state: HypnogramViewerState) => ReactNode;
}) {
  const client = useQueryClient();
  const [jumpInput, setJumpInput] = useState("");
  const [jumpError, setJumpError] = useState("");
  const [layer, setLayer] = useState<Layer>("both");
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(INITIAL_TIME_WINDOW);
  const [suspended, setSuspended] = useState(() => document.visibilityState === "hidden");
  const needsVisibleRestore = useRef(document.visibilityState === "hidden");
  const [resumeWhenReady, setResumeWhenReady] = useState(false);
  const [scrubberPreviewSeconds, setScrubberPreviewSeconds] = useState<number | null>(null);
  const [hoverSeconds, setHoverSeconds] = useState<number | null>(null);
  const localPreferences = useViewerPreferences(props.preferenceIdentity, !props.viewPreferences);
  const [preferences, setPreferences] = props.viewPreferences || localPreferences;
  const hypnogramVisible = preferences.showHypnogram && timeWindow !== "full";
  const orientationPanelId=useId();
  const shellRef=useRef<HTMLElement>(null);
  const [effectiveLabelWidth,setEffectiveLabelWidth]=useState(96);
  useLayoutEffect(()=>{
    const shell=shellRef.current;if(!shell)return;
    const measure=()=>{const signal=shell.querySelector(".signal-table")||shell;
      const available=signal.getBoundingClientRect().width;
      const saved=preferences.labelWidthMode === "compact"?preferences.compactLabelWidth:preferences.expandedLabelWidth;
      setEffectiveLabelWidth(Math.min(saved,Math.max(64,Math.floor(available*.36))));};
    measure();const observer=typeof ResizeObserver === "undefined"?null:new ResizeObserver(measure);observer?.observe(shell);return ()=>observer?.disconnect();
  },[preferences.labelWidthMode,preferences.compactLabelWidth,preferences.expandedLabelWidth,preferences.showOrientationPanel]);
  const [orderOpen,setOrderOpen]=useState(false);
  const sessionIdentity=props.preferenceIdentity+"|"+props.dataSource.key.join("|");
  const [centreState,setCentreState]=useState<{identity:string;values:Record<string,number>}>({identity:sessionIdentity,values:{}});
  const centres=useMemo(()=>centreState.identity===sessionIdentity?centreState.values:{},[centreState,sessionIdentity]);
  const [combinedCamera, setCombinedCamera] = useState<CameraView>(DEFAULT_COMBINED_CAMERA);
  const [cameraViews, setCameraViews] = useState(createDefaultCameraViews);
  const lastCameraSource = useRef<"head" | "torso">("head");
  const lastDetailDuration = useRef<15 | 30 | 60>(30);
  const hoverFrame = useRef<number | null>(null);
  const identity = useMemo(() => props.dataSource.key.join("|"), [props.dataSource.key]);
  const [preCapabilityPosition, setPreCapabilityPosition] = useState({ identity, seconds: 0 });
  const priorIdentity = useRef(identity);
  const priorDataSourceKey = useRef(props.dataSource.key);
  const priorPreferenceIdentity = useRef(props.preferenceIdentity);
  useEffect(() => {
    if (priorIdentity.current !== identity) {
      void client.cancelQueries({ queryKey: priorDataSourceKey.current });
      client.removeQueries({ queryKey: priorDataSourceKey.current });
      setJumpInput(""); setJumpError("");
      setLayer("both"); setTimeWindow(INITIAL_TIME_WINDOW); setHoverSeconds(null); setScrubberPreviewSeconds(null);
      setCombinedCamera(DEFAULT_COMBINED_CAMERA);
      setCameraViews(createDefaultCameraViews()); lastCameraSource.current = "head";
      setSuspended(document.visibilityState === "hidden");
      setResumeWhenReady(false);
      setPreCapabilityPosition({ identity, seconds: 0 });
      priorIdentity.current = identity;
      priorDataSourceKey.current = props.dataSource.key;
    }
  }, [client, identity, props.dataSource.key]);
  useEffect(() => { setJumpInput(""); setJumpError(""); }, [props.preferenceIdentity]);
  priorPreferenceIdentity.current = props.preferenceIdentity;
  useEffect(() => {
    if (!preferences.cursorFollowsMouse) setHoverSeconds(null);
  }, [preferences.cursorFollowsMouse]);
  useEffect(() => () => {
    if (hoverFrame.current != null) window.cancelAnimationFrame(hoverFrame.current);
  }, []);

  const capabilities = useQuery({
    queryKey: [...props.dataSource.key, "capabilities"],
    queryFn: ({ signal }) => props.dataSource.loadCapabilities(signal),
    enabled: !suspended,
    refetchOnWindowFocus: false,
  });
  const inventory=useMemo(()=>displayInventory(props.dataSource.profile,capabilities.data),[props.dataSource.profile,capabilities.data]);
  useEffect(()=>{
    const positions=inventory.filter(c=>c.modalityId.startsWith("position:"));
    if(positions.length!==1)return;
    const oldKey=["track",props.dataSource.profile.profile_id,props.dataSource.profile.profile_version,"position"].map(encodeURIComponent).join("|"),next=positions[0].key;
    setPreferences(p=>{
      if(p.windowVisibility[next] || p.trackVisibility[next]!==undefined || (!p.windowVisibility[oldKey] && p.trackVisibility[oldKey]===undefined))return p;
      return {...p,windowVisibility:{...p.windowVisibility,[next]:p.windowVisibility[oldKey]||{}},trackVisibility:{...p.trackVisibility,...(p.trackVisibility[oldKey]===undefined?{}:{[next]:p.trackVisibility[oldKey]})}};
    });
  },[inventory,props.dataSource.profile,setPreferences]);
  const orderKey=profileOrderKey(props.dataSource.profile);
  const orderedChannels=useMemo(()=>orderedInventory(inventory,preferences.channelOrder[orderKey]),[inventory,preferences.channelOrder,orderKey]);
  const windowKey=String(timeWindow) as DetailWindowKey;
  const visibleChannels=useMemo(()=>orderedChannels.filter(c=>channelVisible(preferences,c,windowKey)),[orderedChannels,preferences.windowVisibility,preferences.channelVisibility,preferences.trackVisibility,windowKey]);
  const distributionVisible = priorPreferenceIdentity.current === props.preferenceIdentity && preferences.showOrientationPanel && preferences.combinedOrientation && preferences.showPositionDistribution && !suspended && !capabilities.isError;
  const distribution = usePositionDistribution(props.dataSource, props.preferenceIdentity, distributionVisible, capabilities.data);
  const dataSourceRef = useRef(props.dataSource);
  dataSourceRef.current = props.dataSource;
  const playbackModalities = useMemo(() => {
    const available = new Map(capabilities.data?.modalities.filter((item) => item.state === "available").map((item) => [item.id, item]) || []);
    return {
      essential: props.dataSource.profile.modalities.filter((item) =>
        item.kind !== "quaternion" && item.kind !== "media" && item.kind !== "unavailable" && available.has(item.id) &&
        (layer === "both" || item.origin === layer) && visibleChannels.some(c=>c.modalityId===item.id)).map((item) => item.id),
      orientation: props.dataSource.profile.modalities.filter((item) =>
        (preferences.showOrientationPanel || visibleChannels.some(c=>c.modalityId===`position:${item.id}` && (layer === "both" || item.origin === layer))) && item.kind === "quaternion" && orientationModelForModality(item.id) != null && available.has(item.id)).map((item) => item.id),
    };
  }, [capabilities.data, layer, visibleChannels, props.dataSource.profile,preferences.showOrientationPanel]);
  const essentialKey = playbackModalities.essential.join(",");
  const orientationKey = playbackModalities.orientation.join(",");
  const playback = useMemo(() => capabilities.data ? new PlaybackController({
    recordingRef: props.dataSource.recordingRef,
    durationSeconds: props.dataSource.durationSeconds,
    capabilities: capabilities.data,
    essentialModalities: essentialKey ? essentialKey.split(",") : [],
    orientationModalities: orientationKey ? orientationKey.split(",") : [],
    loadCapabilities: (signal) => dataSourceRef.current.loadCapabilities(signal),
    loadWindow: (modalities, start, windowDuration, signal) => dataSourceRef.current.loadWindow(modalities, start, windowDuration, signal),
  }) : null, [identity, props.preferenceIdentity, props.dataSource.recordingRef, props.dataSource.durationSeconds,
    capabilities.data?.data_version, capabilities.data?.profile_sha256]);
  useEffect(() => {
    playback?.setModalities(essentialKey ? essentialKey.split(",") : [], orientationKey ? orientationKey.split(",") : []);
  }, [playback, essentialKey, orientationKey]);
  const playbackState = useSyncExternalStore(
    playback?.subscribe || emptyPlaybackSubscribe,
    playback?.getSnapshot || emptyPlaybackSnapshot,
  );
  const selectedSeconds = playback ? playbackState.positionSeconds :
    preCapabilityPosition.identity === identity ? preCapabilityPosition.seconds : 0;
  const playbackActive = playbackState.status === "playing" || playbackState.status === "buffering" || playbackState.status === "seeking";
  useEffect(() => {
    if (playback && preCapabilityPosition.identity === identity && preCapabilityPosition.seconds > 0) {
      playback.seek(preCapabilityPosition.seconds);
    }
  }, [playback, identity, preCapabilityPosition]);
  useEffect(() => () => playback?.dispose(), [playback]);
  useEffect(() => {
    if (!playback || !resumeWhenReady) return;
    setResumeWhenReady(false);
    void playback.play();
  }, [playback, resumeWhenReady]);
  useEffect(() => {
    let disposed = false;
    let returnGeneration = 0;
    let restoring = false;
    const queryKey = dataSourceRef.current.key;
    const pageHide = () => {
      returnGeneration++;
      restoring = false;
      needsVisibleRestore.current = true;
      playback?.suspend();
      setSuspended(true);
      void client.cancelQueries({ queryKey });
    };
    const pageShow = async () => {
      if (document.visibilityState !== "visible") return;
      playback?.visible();
      if (!needsVisibleRestore.current || restoring) return;
      restoring = true;
      const generation = returnGeneration;
      // With no controller yet, enabling the initial capability query below is
      // the access check; dependent reads still wait for its successful result.
      const restored = playback ? await playback.restorePaused() : true;
      if (disposed || generation !== returnGeneration) return;
      restoring = false;
      if (!restored || document.visibilityState !== "visible") return;
      needsVisibleRestore.current = false;
      void client.invalidateQueries({ queryKey, refetchType: "none" });
      setSuspended(false);
    };
    const visibility = () => {
      if (document.visibilityState === "hidden") pageHide();
      else void pageShow();
    };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("pagehide", pageHide);
    window.addEventListener("pageshow", pageShow);
    visibility();
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("pagehide", pageHide);
      window.removeEventListener("pageshow", pageShow);
    };
  }, [client, playback, identity]);
  const modalityHasVisibleChannels=useCallback((modality:SignalProfileV1["modalities"][number])=>visibleChannels.some(c=>c.modalityId===modality.id),[visibleChannels]);
  const requestedModalities = useMemo(() => props.dataSource.profile.modalities
    .filter((item) => item.kind !== "quaternion" && item.kind !== "media" && item.kind !== "unavailable")
    .filter((item) => layer === "both" || item.origin === layer)
    .filter(modalityHasVisibleChannels)
    .map((item) => item.id), [layer, modalityHasVisibleChannels, props.dataSource.profile]);
  const overview = useQuery({
    queryKey: [...props.dataSource.key, "overview", requestedModalities.slice().sort().join(","), 8000],
    queryFn: ({ signal }) => props.dataSource.loadOverview(requestedModalities, 8_000, signal),
    enabled: !suspended && !playbackState.requiresResume && Boolean(capabilities.data && requestedModalities.length),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const duration: 15 | 30 | 60 = timeWindow === "full" ? 30 : timeWindow;
  if (timeWindow !== "full") lastDetailDuration.current = timeWindow;
  const windowStart = playbackActive
    ? Math.floor(Math.min(selectedSeconds, Math.max(0, props.dataSource.durationSeconds - 1e-9)) / PLAYBACK_TILE_SECONDS) * PLAYBACK_TILE_SECONDS
    : detailWindowStart(selectedSeconds, duration, props.dataSource.durationSeconds);
  const detail = useQuery({
    queryKey: [...props.dataSource.key, "window", requestedModalities.slice().sort().join(","), windowStart, duration],
    queryFn: async ({ signal }) => {
      const result = await props.dataSource.loadWindow(requestedModalities, windowStart, duration, signal);
      if (!isCurrentSignalWindow(result, {
        recordingRef: props.dataSource.recordingRef,
        dataVersion: capabilities.data?.data_version || "", startSeconds: windowStart, durationSeconds: duration,
      })) throw new DOMException("Obsolete signal response", "AbortError");
      return result;
    },
    enabled: !suspended && !playbackActive && !playbackState.requiresResume && timeWindow !== "full" && Boolean(capabilities.data && requestedModalities.length),
    gcTime: 5 * 60_000,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  const orientationModalities = props.dataSource.profile.modalities.filter(
    (item): item is QuaternionSignalModalityV1 => item.kind === "quaternion" && orientationModelForModality(item.id) != null,
  );
  const positionVisible=visibleChannels.some(c=>c.modalityId.startsWith("position:")&&(layer === "both" || layer === "recorded"));
  const orientationNeeded=orientationModalities.filter(item=>preferences.showOrientationPanel || visibleChannels.some(c=>c.modalityId===`position:${item.id}` && (layer === "both" || item.origin === layer)));
  const orientationDuration:15|30|60=positionVisible&&timeWindow!=="full"?duration:15;
  const orientationStart = playbackActive || positionVisible&&timeWindow!=="full" ? windowStart : Math.max(0, Math.min(Math.max(0, props.dataSource.durationSeconds - 15), selectedSeconds - 7.5));
  const orientation = useQuery({
    queryKey: [...props.dataSource.key, "window", "orientation", orientationStart, orientationDuration, orientationNeeded.map((item) => item.id).join(",")],
    queryFn: async ({ signal }) => {
      const result=await props.dataSource.loadWindow(orientationNeeded.map((item) => item.id), orientationStart, orientationDuration, signal);
      if (!isCurrentSignalWindow(result,{recordingRef:props.dataSource.recordingRef,dataVersion:capabilities.data?.data_version || "",startSeconds:orientationStart,durationSeconds:orientationDuration}) || result.profile_version !== props.dataSource.profile.profile_version) throw new DOMException("Obsolete orientation response","AbortError");
      return result;
    },
    enabled: (preferences.showOrientationPanel || positionVisible && timeWindow!=="full") && !suspended && !playbackActive && !playbackState.requiresResume && Boolean(capabilities.data && orientationNeeded.some((modality) =>
      capabilities.data?.modalities.some((item) => item.id === modality.id && item.state === "available"))),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (detail.data || orientation.data) trimSignalWindowCache(client, props.dataSource.key);
  }, [client, detail.data, orientation.data, props.dataSource.key]);

  const accessDenied=[capabilities.error,overview.error,detail.error,orientation.error].some((error) =>
    error && typeof error === "object" && "status" in error && [401,403,404].includes(Number(error.status)));
  useEffect(() => {
    if (!accessDenied) return;
    playback?.suspend();
    void client.cancelQueries({queryKey:props.dataSource.key});
  }, [accessDenied,playback,client,identity]);

  function updateViewerState(action: () => void) {
    action();
  }
  const seek = useCallback((seconds: number) => {
    setHoverSeconds(null);
    setScrubberPreviewSeconds(null);
    const selected = clampSelectedTime(seconds, props.dataSource.durationSeconds);
    if (playback) playback.seek(selected);
    else setPreCapabilityPosition({ identity, seconds: selected });
  }, [identity, playback, props.dataSource.durationSeconds]);
  function seekFromHypnogram(seconds: number) {
    seek(seconds);
    if (timeWindow === "full") setTimeWindow(lastDetailDuration.current);
  }
  async function startPlayback() {
    if (accessDenied) return;
    setTimeWindow(30);
    if (!playback && suspended) {
      const result = await capabilities.refetch();
      if (!result.isError && document.visibilityState === "visible") {
        setResumeWhenReady(true);
        setSuspended(false);
      }
      return;
    }
    await playback?.play();
    if (playback && !playback.getSnapshot().requiresResume && playback.getSnapshot().status !== "error") setSuspended(false);
  }

  function previewAtPointer(event: ReactPointerEvent<HTMLDivElement>) {
    if (!preferences.cursorFollowsMouse) return;
    if (!Number.isFinite(event.clientX)) return;
    const target = event.target instanceof Element ? event.target : null;
    const plot = target?.closest<HTMLElement>(".signal-track-plot");
    if (!plot || !event.currentTarget.contains(plot)) {
      setHoverSeconds(null);
      return;
    }
    const bounds = plot.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || bounds.width <= 0) {
      setHoverSeconds(null);
      return;
    }
    const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
    const preview = interval.start_seconds + ratio * (interval.end_seconds - interval.start_seconds);
    if (hoverFrame.current != null) window.cancelAnimationFrame(hoverFrame.current);
    hoverFrame.current = window.requestAnimationFrame(() => {
      setHoverSeconds(preview);
      hoverFrame.current = null;
    });
  }
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const key = event.key;
      if (key !== "ArrowLeft" && key !== "ArrowRight" && key !== "ArrowUp" && key !== "ArrowDown") return;
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || hasNativeArrowKeyBehavior(event.target)) return;
      event.preventDefault();
      const next = applyViewerArrowKey(
        { selectedSeconds, timeWindow },
        key,
        props.dataSource.durationSeconds,
      );
      updateViewerState(() => {
        seek(next.selectedSeconds);
        setTimeWindow(next.timeWindow);
      });
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [identity, seek, selectedSeconds, timeWindow, props.dataSource.durationSeconds]);

  function changeCameraView(source: "head" | "torso", cameraView: CameraView) {
    lastCameraSource.current = source;
    setCameraViews((current) => updateCameraViews(current, source, cameraView, preferences.link3dViews));
  }
  const displayed = accessDenied || suspended || playbackState.requiresResume ? undefined : playbackActive ? playback?.windowAt(selectedSeconds) : timeWindow === "full" ? overview.data : detail.data;
  const interval = displayed?.interval || (!playbackActive && timeWindow === "full"
    ? { start_seconds: 0, end_seconds: props.dataSource.durationSeconds }
    : { start_seconds: windowStart, end_seconds: Math.min(props.dataSource.durationSeconds, windowStart + duration) });
  const capabilityById = useMemo(() => new Map((capabilities.data?.modalities || []).map((item) => [item.id, item])), [capabilities.data]);
  const modalities = useMemo(() => [...props.dataSource.profile.modalities,
    ...inventory.filter(c=>c.modalityId.startsWith("position:")).map(c=>({id:c.modalityId,label:c.longName,kind:"unavailable" as const,origin:"recorded" as const,order:999,required:false,state:"not_computed" as const,reason_code:"DISPLAY_ONLY",fallback:"show_status" as const}))]
    .filter((item) => item.kind !== "quaternion" && item.kind !== "media")
    .filter((item) => capabilityById.get(item.id)?.state === "available" || item.fallback === "show_status")
    .filter((item) => layer === "both" || item.origin === layer)
    .filter(modalityHasVisibleChannels)
    .sort((left, right) => left.order - right.order), [props.dataSource.profile, inventory, capabilityById, layer, modalityHasVisibleChannels]);
  const series = useMemo(() => displayed?.series || [], [displayed]);
  const displayedSeconds = scrubberPreviewSeconds ?? hoverSeconds ?? selectedSeconds;
  const cursorRatio = Math.max(0, Math.min(1, (displayedSeconds - interval.start_seconds) /
    Math.max(1e-9, interval.end_seconds - interval.start_seconds)));
  const trackRows = useMemo(() => {
    const rows=modalities.flatMap(modality=>{
      const capability=capabilityById.get(modality.id);
      const channels=visibleChannels.filter(c=>c.modalityId===modality.id);
      const modalitySeries=series.filter(item=>item.modality_id===modality.id&&(item.representation === "intervals" || !capability?.channels.length || channels.some(c=>c.channelId===item.channel_id)));
      if(!preferences.separateMultichannelSignals || modality.kind !== "multichannel" || (capability?.channels.length||0)<=1) {
        return [{key:channels[0]?.key||modality.id,label:channels.length===1?channelName(channels[0],preferences):modality.label,modality,capability,channels,series:modalitySeries}];
      }
      return channels.map(channel=>({key:channel.key,label:channelName(channel,preferences),modality,capability:capability?{...capability,channels:capability.channels.filter(c=>c.id===channel.channelId)}:undefined,channels:[channel],series:modalitySeries.filter(item=>item.channel_id===channel.channelId)}));
    });
    return rows.sort((a,b)=>orderedChannels.findIndex(c=>c.key===a.key)-orderedChannels.findIndex(c=>c.key===b.key));
  },[modalities,capabilityById,series,visibleChannels,orderedChannels,preferences.separateMultichannelSignals,preferences.channelDisplay,preferences.labelWidthMode]);
  function recenter(channel:DisplayChannel,reset=false) {
    const source=series.find((s):s is SignalNativeSeriesV1|SignalMinMaxSeriesV1=>s.representation!=="intervals"&&s.channel_id===channel.channelId&&s.modality_id===channel.modalityId);
    const range=source?finiteVisibleRange(source,interval):null;
    setCentreState(current=>{const values=current.identity===sessionIdentity?{...current.values}:{};
      if(reset || preferences.channelDisplay[channel.key]?.scaleMode!=="fixed")delete values[channel.key];else if(range)values[channel.key]=range.centre;
      return {identity:sessionIdentity,values};});
  }
  useEffect(()=>{
    setCentreState(current=>{
      const values=current.identity===sessionIdentity?{...current.values}:{};let changed=current.identity!==sessionIdentity;
      for(const key of Object.keys(values))if(preferences.channelDisplay[key]?.scaleMode!=="fixed"){delete values[key];changed=true;}
      for(const c of inventory) {
        if(preferences.channelDisplay[c.key]?.scaleMode!=="fixed" || values[c.key]!==undefined)continue;
        const source=series.find((s):s is SignalNativeSeriesV1|SignalMinMaxSeriesV1=>s.representation!=="intervals"&&s.modality_id===c.modalityId&&s.channel_id===c.channelId);
        const range=source?finiteVisibleRange(source,interval):null;
        if(range){values[c.key]=range.centre;changed=true;}
      }
      return changed?{identity:sessionIdentity,values}:current;
    });
  },[sessionIdentity,inventory,series,interval,preferences.channelDisplay]);
  const positionWindow=accessDenied||capabilities.isError||suspended||playbackState.requiresResume||orientation.isError||playbackState.status === "error"?undefined:playbackActive?playback?.orientationAt(selectedSeconds)||undefined:orientation.data||playback?.orientationAt(selectedSeconds)||undefined;
  const positionSourceKey=visibleChannels.filter(c=>c.modalityId.startsWith("position:")).map(c=>c.modalityId).join(",");
  const positionData=useMemo(()=>new Map(orientationModalities.filter(m=>(layer === "both" || m.origin === layer) && (playbackActive || timeWindow!=="full") && capabilityById.get(m.id)?.state === "available" && visibleChannels.some(c=>c.modalityId===`position:${m.id}`)).map(m=>[m.id,positionSamples(positionWindow,m,preferences.verticalPositionThresholdDegrees)])),[positionWindow,props.dataSource.profile,capabilityById,layer,playbackActive,timeWindow,positionSourceKey,preferences.verticalPositionThresholdDegrees]);
  function positionReadout(id:string):string {
    if(accessDenied || capabilities.isError || suspended || playbackState.requiresResume || orientation.isError || playbackState.status === "error")return "Withheld · access or data request unavailable";
    const modality=orientationModalities.find(m=>m.id===id), config=orientationModelForModality(id);
    if(!positionWindow||!modality||!config||capabilityById.get(id)?.state!=="available")return "Missing";
    const value=positionValue(orientationAtTime(positionWindow,modality,displayedSeconds,config.calibrationWxyz),preferences.verticalPositionThresholdDegrees);
    return value.angle===null?value.state:`${QUADRANTS[value.quadrant!]} · ${value.angle.toFixed(1)}°`;
  }
  const orientationByModel = new Map<string, OrientationSelection>();
  for (const config of ORIENTATION_MODELS) {
    const modality = orientationModalities.find((item) => config.modalityIds.includes(item.id));
    const orientationSeconds = displayedSeconds;
    // The controller owns bounded same-source tiles; pause retains them, whereas
    // seek, modality/source change, suspension and disposal clear them. Prefer a
    // completed manual read (including truthful gaps) over the handoff tile.
    const orientationWindow = playbackActive ? playback?.orientationAt(orientationSeconds) : orientation.data || playback?.orientationAt(orientationSeconds);
    const reason = accessDenied || playbackState.status === "error" ? "access_unvalidated" : suspended || playbackState.requiresResume ? "suspended" :
      capabilities.isError || orientation.isError ? "access_unvalidated" :
      !modality || capabilityById.get(modality.id)?.state !== "available" ? "sensor_absent" : null;
    if (reason) orientationByModel.set(config.id,{state:"unavailable",reason});
    else if (orientationWindow && modality) orientationByModel.set(config.id, orientationAtTime(orientationWindow, modality, orientationSeconds, config.calibrationWxyz));
    else orientationByModel.set(config.id,{state:"unavailable",reason:"loading"});
  }
  const availableCount = capabilities.data?.modalities.filter((item) => item.state === "available").length || 0;
  const unavailableCount = (capabilities.data?.modalities.length || 0) - availableCount;
  const statusCapabilitiesLoaded = Boolean(capabilities.data && !accessDenied && !suspended && !playbackState.requiresResume && !capabilities.isError);
  useEffect(() => {
    props.onStatusInfoChange?.({ sourceKey: identity, recordingRef: props.dataSource.recordingRef, dataVersion: capabilities.data?.data_version || null,
      availableCount, unavailableCount, capabilitiesLoaded: statusCapabilitiesLoaded, playbackActive });
  }, [props.onStatusInfoChange, identity, props.dataSource.recordingRef, availableCount, unavailableCount, capabilities.data?.data_version, statusCapabilitiesLoaded, playbackActive]);

  const exportController=useRef<AbortController|null>(null);
  const printCleanup=useRef<(()=>void)|null>(null);
  const exportHost=useRef<HTMLDivElement|null>(null);
  const [exportStatus,setExportStatus]=useState("");
  useEffect(()=>{
    if(exportStatus !== "Print / Save as PDF opened.")return;
    const timer=window.setTimeout(()=>setExportStatus(""),5000);
    return ()=>window.clearTimeout(timer);
  },[exportStatus]);
  const [exportSnapshot,setExportSnapshot]=useState<{source:ViewerDataSource;caps:RecordingCapabilitiesV1;overview:SignalOverviewV1;preferences:ViewerPreferences;centres:Record<string,number>;hypnogram:typeof props.renderHypnogram;title:string}|null>(null);
  const cancelExport=useCallback(()=>{exportController.current?.abort();exportController.current=null;printCleanup.current?.();printCleanup.current=null;},[]);
  useLayoutEffect(()=>{setExportSnapshot(null);setExportStatus("");return cancelExport;},[sessionIdentity,JSON.stringify(props.dataSource.profile),cancelExport]);
  useEffect(()=>{if(accessDenied||capabilities.isError||suspended){cancelExport();setExportSnapshot(null);setExportStatus("");}},[accessDenied,capabilities.isError,suspended,cancelExport]);
  async function prepareExport(){
    if(exportController.current)return;
    printCleanup.current?.();printCleanup.current=null;
    const controller=new AbortController();exportController.current=controller;
    setExportStatus("Preparing full-night PDF…");
    const source=props.dataSource, saved=structuredClone(preferences), savedCentres={...centres}, hypnogram=props.renderHypnogram;
    try{
      await source.revalidateExport?.(controller.signal);
      const caps=await source.loadCapabilities(controller.signal);
      if(caps.recording_ref!==source.recordingRef||caps.duration_seconds!==source.durationSeconds||caps.profile_version!==source.profile.profile_version||caps.data_version!==capabilities.data?.data_version||caps.profile_sha256!==capabilities.data?.profile_sha256)throw Error("Recording changed. Refresh and try again.");
      const channels=displayInventory(source.profile,caps).filter(c=>channelVisible(saved,c,"full"));
      const ids=[...new Set(channels.map(c=>c.modalityId))].filter(id=>!id.startsWith("position:")&&source.profile.modalities.some(m=>m.id===id&&m.kind!=="quaternion"&&m.kind!=="media"&&m.kind!=="unavailable"));
      const overview=await (source.loadExportOverview||source.loadOverview)(ids,8000,controller.signal);
      await source.revalidateExport?.(controller.signal);
      const finalCaps=await source.loadCapabilities(controller.signal);
      if(JSON.stringify(caps)!==JSON.stringify(finalCaps) && (caps.data_version!==finalCaps.data_version||caps.profile_sha256!==finalCaps.profile_sha256||JSON.stringify(caps.modalities)!==JSON.stringify(finalCaps.modalities)))throw Error("Recording changed during preparation. Try again.");
      if(overview.recording_ref!==source.recordingRef||overview.data_version!==caps.data_version||overview.profile_version!==source.profile.profile_version||(overview.profile_sha256!==undefined&&overview.profile_sha256!==caps.profile_sha256)||overview.interval.start_seconds!==0||overview.interval.end_seconds!==source.durationSeconds)throw Error("Full-night source changed or returned an incomplete overview. Try again.");
      controller.signal.throwIfAborted();
      setExportSnapshot({source,caps,overview,preferences:saved,centres:savedCentres,hypnogram,title:`${props.recordingLabel||source.recordingRef} · ${caps.time_origin||"date unavailable"} · ${formatRecordingTime(source.durationSeconds,true)}`});
    }catch(error){if(!controller.signal.aborted)setExportStatus(`PDF preparation failed: ${error instanceof Error?error.message:"Try again."}`);if(exportController.current===controller)exportController.current=null;}
  }
  useEffect(()=>{
    if(!exportSnapshot||!exportHost.current)return;
    const controller=exportController.current;if(!controller)return;
    let frame=0;
    // React commits all rows before layout painters; give ResizeObserver a frame.
    frame=requestAnimationFrame(()=>{frame=requestAnimationFrame(()=>{void (async()=>{
      try{printCleanup.current=await openFullNightPrint(exportHost.current!,exportSnapshot.title,controller.signal,async()=>{
        await exportSnapshot.source.revalidateExport?.(controller.signal);
        const current=await exportSnapshot.source.loadCapabilities(controller.signal);
        if(current.recording_ref!==exportSnapshot.caps.recording_ref||current.data_version!==exportSnapshot.caps.data_version||current.profile_sha256!==exportSnapshot.caps.profile_sha256||JSON.stringify(current.modalities)!==JSON.stringify(exportSnapshot.caps.modalities))throw Error("Recording changed before printing. Try again.");
      });if(!controller.signal.aborted)setExportStatus("Print / Save as PDF opened.");}
      catch(error){if(!controller.signal.aborted)setExportStatus(`PDF preparation failed: ${error instanceof Error?error.message:"Try again."}`);}
      finally{if(exportController.current===controller){exportController.current=null;setExportSnapshot(null);}}
    })();});});
    return ()=>cancelAnimationFrame(frame);
  },[exportSnapshot]);
  const exportChannels=exportSnapshot?orderedInventory(displayInventory(exportSnapshot.source.profile,exportSnapshot.caps),exportSnapshot.preferences.channelOrder[profileOrderKey(exportSnapshot.source.profile)]).filter(c=>channelVisible(exportSnapshot.preferences,c,"full")):[];
  const exportInterval={start_seconds:0,end_seconds:exportSnapshot?.source.durationSeconds||1};
  const exportRows:DisplayChannel[][]=[];
  for(const channel of exportChannels){
    const existing=!exportSnapshot?.preferences.separateMultichannelSignals&&exportRows.find(row=>row[0].modalityId===channel.modalityId);
    if(existing)existing.push(channel);else exportRows.push([channel]);
  }
  return (
    <>
    <article className="data-viewer-shell" data-orientation-visible={preferences.showOrientationPanel} ref={shellRef} style={{"--track-label-width":`${effectiveLabelWidth}px`,"--hypnogram-height":`${preferences.hypnogramHeight}px`} as CSSProperties}>
      {props.summary}
      {props.provenance}
      {props.renderHypnogram && hypnogramVisible && props.renderHypnogram({
        selectedSeconds, displayedSeconds, onPreview: setHoverSeconds,
        visibleStartSeconds: windowStart,
        visibleEndSeconds: Math.min(props.dataSource.durationSeconds, windowStart + (playbackActive ? PLAYBACK_TILE_SECONDS : duration)),
        fullNight: false, onSeek: seekFromHypnogram,
      })}
      <section className="viewer-controls" aria-label="Signal viewer controls">
        {props.toolbar}
        <fieldset><legend>Layer</legend><div className="layer-toggle">{(["both", "recorded", "derived"] as const).map((item) =>
          <button aria-pressed={layer === item} key={item} onClick={() => updateViewerState(() => setLayer(item))} type="button">{item}</button>)}</div></fieldset>
        <label><span>Detail window</span><select disabled={playbackActive} value={timeWindow} onChange={(event) => updateViewerState(() => setTimeWindow(event.target.value === "full" ? "full" : Number(event.target.value) as 15 | 30 | 60))}>
          <option value="full">Full night</option><option value={15}>15 seconds</option><option value={30}>30 seconds</option><option value={60}>60 seconds</option>
        </select></label>
        <div className="playback-controls" aria-label="Recorded playback controls"
          data-buffered-bytes={playback?.bufferedBytes || 0} data-outstanding-requests={playback?.outstandingRequests || 0}>
          <button data-tooltip={playbackActive?"Pause":playbackState.status === "ended"?"Replay":playbackState.requiresResume||suspended?"Resume":"Play"} aria-label={playbackActive?"Pause":playbackState.status === "ended"?"Replay":playbackState.requiresResume||suspended?"Resume":"Play"} title={playbackActive?"Pause":playbackState.status === "ended"?"Replay":playbackState.requiresResume||suspended?"Resume":"Play"} className="icon-button" disabled={accessDenied || (playbackState.requiresResume && playbackState.status === "seeking") || (!suspended && (!capabilities.data || !(playbackModalities.essential.length || playbackModalities.orientation.length)))} onClick={() => {
            if (playbackActive) playback?.pause();
            else void startPlayback();
          }} type="button"><ViewerIcon name={playbackActive ? "Pause" : playbackState.status === "ended" ? "Replay" : playbackState.requiresResume || suspended ? "Resume" : "Play"}/></button>
          <label><span>Rate</span><select aria-label="Playback rate" onChange={(event) => playback?.setRate(Number(event.target.value) as PlaybackRate)} value={playbackState.rate}>
            {PLAYBACK_RATES.map((rate) => <option key={rate} value={rate}>{rate}×</option>)}
          </select></label>
          <div className="playback-readout">
            {preferences.showSelectedTotalTime && <output aria-label="Selected time / recording duration">
              {formatRecordingTime(displayedSeconds, true)} <span>/ {formatRecordingTime(props.dataSource.durationSeconds, true)}</span>
            </output>}
          <span aria-live="polite" className="playback-state" role="status">
            {playbackState.requiresResume ? playbackState.status === "seeking" ? "Revalidating recording access…" : playbackState.status === "error" ? "Access revalidation failed" : "Paused after tab change · access revalidation required" :
              playbackState.status === "buffering" || playbackState.status === "seeking" ? "Buffering…" :
                playbackState.status === "playing" ? "Playing" :
                  playbackState.status === "ended" ? "End of recording" :
                    playbackState.status === "error" ? "Playback error" : "Paused"}
          </span>
          </div>
        </div>
        <form className="jump-to-time" onSubmit={(event) => {
          event.preventDefault();
          try { const seconds=parseJumpTime(jumpInput,props.dataSource.durationSeconds); setJumpError(""); seek(seconds); }
          catch (error) { setJumpError(error instanceof Error ? error.message : "Invalid time"); }
        }}>
          <label><span>Jump to time</span><input aria-describedby={jumpError ? "jump-format jump-error" : "jump-format"} aria-invalid={Boolean(jumpError)} value={jumpInput} onChange={(event) => { setJumpInput(event.target.value); setJumpError(""); }} placeholder="HH:MM:SS or seconds" /></label>
          <button type="submit">Go</button>
          <small className="viewer-input-help" id="jump-format">HH:MM:SS[.fraction] (hours may exceed 23), or seconds such as 10933.04</small>
          {jumpError && <span className="viewer-control-message" id="jump-error" role="alert">{jumpError}</span>}
        </form>
        <button className="reset-view" onClick={() => updateViewerState(() => { seek(0); setTimeWindow(INITIAL_TIME_WINDOW); })} type="button">Reset view</button>
        <div className="viewer-shortcuts">
        <div className="pdf-export-control">
          <button className="icon-button" type="button" aria-label="Export full-night PDF" title="Export full-night PDF · opens Print / Save as PDF" data-tooltip="Export full-night PDF · opens Print / Save as PDF" disabled={Boolean(exportSnapshot)||exportStatus.startsWith("Preparing")||accessDenied||suspended||capabilities.isError} onClick={()=>void prepareExport()}><ViewerIcon name="pdf"/></button>
          {exportStatus&&<div className="viewer-control-message pdf-export-status"><span role="status">{exportStatus}</span>{!exportStatus.startsWith("Preparing")&&<button type="button" aria-label="Dismiss PDF status" onClick={()=>setExportStatus("")}>×</button>}</div>}
        </div>
        <ViewerSettings key={props.preferenceIdentity} preferences={preferences} setPreferences={setPreferences} profile={props.dataSource.profile} capabilities={capabilities.data} hasHypnogram={Boolean(props.renderHypnogram)} pageCards={props.pageCards} onLinkViews={() => setCameraViews((current) => linkCameraViews(current, lastCameraSource.current))} />
        <button className="icon-button" type="button" data-tooltip={!props.renderHypnogram?"Standalone hypnogram unavailable":timeWindow === "full"?"Standalone hypnogram hidden in Full night":"Show or hide standalone hypnogram"} aria-label={props.renderHypnogram?"Show standalone hypnogram":"Standalone hypnogram unavailable in this viewer"} title={!props.renderHypnogram?"Standalone hypnogram unavailable in this viewer":timeWindow === "full"?"Standalone hypnogram hidden in Full night":"Show or hide standalone hypnogram"} aria-pressed={hypnogramVisible} disabled={!props.renderHypnogram || timeWindow === "full"} onClick={()=>setPreferences(p=>({...p,showHypnogram:!p.showHypnogram}))}><ViewerIcon name="hypnogram"/></button>
        <button className="icon-button" type="button" data-tooltip="Toggle compact / expanded labels" aria-label="Expanded channel labels" title="Toggle compact / expanded channel labels" aria-pressed={preferences.labelWidthMode === "expanded"} onClick={()=>setPreferences(p=>({...p,labelWidthMode:p.labelWidthMode === "compact"?"expanded":"compact"}))}><ViewerIcon name="width"/></button>
        <button className="icon-button" type="button" data-tooltip="Channel order" aria-label="Channel order" title="Channel order" aria-haspopup="dialog" aria-expanded={orderOpen} onClick={()=>setOrderOpen(true)}><ViewerIcon name="order"/></button>
        <button className="icon-button" type="button" data-tooltip="Show or hide head and torso panel" aria-label="Show orientation panel" title="Show or hide head and torso panel" aria-controls={orientationPanelId} aria-pressed={preferences.showOrientationPanel} onClick={()=>setPreferences(p=>({...p,showOrientationPanel:!p.showOrientationPanel}))}><ViewerIcon name="orientation"/></button>
        </div>
        {orderOpen&&<ChannelOrderDialog key={sessionIdentity} inventory={inventory} preferences={preferences} setPreferences={setPreferences} profileKey={orderKey} window={windowKey} onClose={()=>setOrderOpen(false)}/>}
      </section>
      <div className="viewer-transport">
        <div className={`signal-transport${preferences.alignTimelineToSignalViewport ? " timeline-aligned" : ""}`}>
          <FullNightTimeline
            detailDuration={!playbackActive && timeWindow === "full" ? null : playbackActive ? PLAYBACK_TILE_SECONDS : duration}
            detailStart={windowStart}
            duration={props.dataSource.durationSeconds}
            onCommit={seek}
            onPreview={(seconds) => setScrubberPreviewSeconds(seconds == null ? null : clampSelectedTime(seconds, props.dataSource.durationSeconds))}
            selected={displayedSeconds}
          />
        </div>

      </div>
      {accessDenied && <Alert className="viewer-error">Recording access is unavailable. Protected data is withheld; reopen the recording after access is restored.</Alert>}
      {props.attention && <Alert className="viewer-attention">{props.attention}</Alert>}
      {capabilities.isPending && <p className="viewer-loading"><Spinner /> Resolving pinned signal capabilities…</p>}
      {capabilities.isError && <Alert className="viewer-error">The recording capabilities could not be loaded. <button onClick={() => void capabilities.refetch()} type="button">Retry</button></Alert>}
      {!playbackActive && capabilities.data && (timeWindow === "full" ? overview.isError : detail.isError) && <Alert className="viewer-error">The requested signal view is temporarily unavailable. <button onClick={() => void (timeWindow === "full" ? overview.refetch() : detail.refetch())} type="button">Retry</button></Alert>}
      {playbackState.error && <Alert className="viewer-error">{playbackState.error} <button onClick={() => void startPlayback()} type="button">Retry playback</button></Alert>}
      {capabilities.data && (
        <div className="viewer-grid">
          <section className="viewer-orientation-area" id={orientationPanelId} aria-label="Head and torso orientation and position" hidden={!preferences.showOrientationPanel}>{preferences.showOrientationPanel && <aside className="viewer-auxiliary">
            {!preferences.combinedOrientation && <div className="orientation-controls" aria-label="Orientation view controls">
              <button onClick={() => setCameraViews(createDefaultCameraViews())} type="button">Reset camera</button>
            </div>}
            {preferences.combinedOrientation ? <CombinedOrientation
              key={`combined:${identity}:${props.preferenceIdentity}`}
              cameraView={combinedCamera} onCameraViewChange={setCombinedCamera}
              onResetCamera={() => setCombinedCamera({ ...DEFAULT_COMBINED_CAMERA })}
              selections={{ head: orientationByModel.get("head") || null, torso: orientationByModel.get("torso") || null }}
              distribution={distributionVisible ? {
                summary: !distribution.isError ? distribution.data : undefined,
                smoothing: preferences.angularSmoothing,
                mode: preferences.distributionTimeMode, colour: preferences.distributionColourMode, units:preferences.distributionUnits,
                displayedSeconds, reason: distribution.reason,
                status: distribution.unavailable ? "unavailable" : distribution.isError ? "error" : !distribution.data && (distribution.isPending || distribution.isFetching) ? "loading" : "ready",
                retry: () => { void distribution.refetch(); },
              } : undefined}
              showExplanation={preferences.showOrientationExplanation}
              appearance={preferences.combinedAppearance}
              gravityArrowColor={preferences.gravityArrowColor} gravityLegendText={preferences.gravityLegendText}
              showGravityDirection={preferences.showGravityDirection} showGravityLegend={preferences.showGravityLegend}
              verticalPositionThresholdDegrees={preferences.verticalPositionThresholdDegrees}
            /> : ORIENTATION_MODELS.map((config) => <OrientationPanel
              showExplanation={preferences.showOrientationExplanation}
              appearance={preferences.modelAppearance[config.id]}
              cameraView={cameraViews[config.id]}
              config={config}
              key={config.id}
              onCameraViewChange={(cameraView) => changeCameraView(config.id, cameraView)}
              selection={orientationByModel.get(config.id) || null}
              gravityArrowColor={preferences.gravityArrowColor}
              gravityLegendText={preferences.gravityLegendText}
              showGravityDirection={preferences.showGravityDirection}
              showGravityLegend={preferences.showGravityLegend}
              suppressRelativeHeading={preferences.suppressRelativeHeading}
              verticalPositionThresholdDegrees={preferences.verticalPositionThresholdDegrees}
            />)}
          </aside>}</section>
          <section className="signal-table" aria-label="Synchronized signal tracks">
            <div className="signal-tracks" data-cursor-preview={hoverSeconds != null ? "true" : "false"} onPointerLeave={() => {
              if (hoverFrame.current != null) window.cancelAnimationFrame(hoverFrame.current);
              hoverFrame.current = null;
              setHoverSeconds(null);
            }} onPointerMove={previewAtPointer}>
              <TimeAxis interval={interval} />
              {trackRows.map((row) => <TrackRow
                capability={row.capability} gap={playbackActive && Boolean(displayed)} highlightActiveStage={playbackActive || preferences.highlightActiveSleepStage} interval={interval} key={row.key}
                label={row.label} loading={playbackActive ? !displayed : timeWindow === "full" ? overview.isPending : detail.isPending} modality={row.modality}
                onSeek={seek} suspended={suspended || playbackState.requiresResume} requestError={playbackState.status === "error" || (!playbackActive && (timeWindow === "full" ? overview.isError : detail.isError))}
                channels={row.channels} preferences={preferences} setPreferences={setPreferences} centres={centres} onRecenter={recenter}
                selectedSeconds={displayedSeconds} series={row.series}
                fullNight={!playbackActive && timeWindow === "full"}
                positionData={positionData.get(row.modality.id.slice(9))} positionReadout={row.modality.id.startsWith("position:")?positionReadout(row.modality.id.slice(9)):undefined}
              />)}
              {trackRows.length === 0 && <div className="all-channels-hidden" role="status">All signal channels are hidden. Timeline, settings, and orientation remain available.</div>}
              <i aria-hidden="true" className="shared-track-cursor" data-testid="shared-track-cursor" style={{ left: `calc(var(--track-label-width) + (100% - var(--track-label-width)) * ${cursorRatio})` }} />
            </div>
            {!props.onStatusInfoChange && <footer className="signal-table-footer"><i /><span>{playbackActive ? "Buffered synchronized view" : "Manual synchronized view"} · native recording time</span></footer>}
          </section>

        </div>
      )}
      {!props.onStatusInfoChange && <footer className="viewer-quality-footer">
        <span>{props.qualityLabel || "Recording quality"} · {availableCount} available · {unavailableCount} unavailable</span>
        <span>Selected time synchronizes signals, Sleep stage, head and torso</span>
      </footer>}
    </article>
    {exportSnapshot&&<div className="full-night-export" ref={node=>{exportHost.current=node;node?.setAttribute("inert","");}} aria-hidden="true" style={{position:"absolute",left:-20000,top:0,width:1080}}>
      <style>{fullNightPrintCss}</style>
      <TimeAxis interval={exportInterval}/>
      {exportSnapshot.preferences.showHypnogram&&exportSnapshot.hypnogram&&<div data-print-row>{exportSnapshot.hypnogram({fullNight:true,visibleStartSeconds:0,visibleEndSeconds:exportInterval.end_seconds,displayedSeconds:0,selectedSeconds:0,onPreview:()=>{},onSeek:()=>{}})}</div>}
      {exportRows.map(channels=>{
        const channel=channels[0];
        const modality=exportSnapshot.source.profile.modalities.find(m=>m.id===channel.modalityId)||{id:channel.modalityId,label:channel.longName,kind:"unavailable" as const,origin:"recorded" as const,order:999,state:"not_computed" as const,reason_code:"DISPLAY_ONLY",required:false,fallback:"show_status" as const};
        const series=exportSnapshot.overview.series.filter(s=>s.modality_id===channel.modalityId&&(s.representation==="intervals"||channels.some(c=>!c.channelId||s.channel_id===c.channelId)));
        return <div data-print-row key={channel.key}><TrackRow label={channels.length===1?channelName(channel,exportSnapshot.preferences):modality.label} capability={exportSnapshot.caps.modalities.find(c=>c.id===channel.modalityId)} channels={channels} preferences={exportSnapshot.preferences} setPreferences={()=>{}} centres={exportSnapshot.centres} onRecenter={()=>{}} modality={modality} requestError={false} suspended={false} selectedSeconds={0} series={series} fullNight highlightActiveStage={false} gap={false} interval={exportInterval} loading={false} onSeek={()=>{}}/><div className="print-unit">{channels.map(c=><span key={c.key} style={{borderLeft:`4px solid ${exportSnapshot.preferences.channelDisplay[c.key]?.colour||c.defaultColour||TRACE_PALETTE[0]}`,paddingLeft:4,marginRight:12}}>{channels.length>1?`${channelName(c,exportSnapshot.preferences)} · `:""}{c.unit||"No unit"} · {exportSnapshot.preferences.channelDisplay[c.key]?.scaleMode==="fixed"?`Fixed span ${exportSnapshot.preferences.channelDisplay[c.key]?.span} ${c.unit||""}`:"Automatic scale"}</span>)}</div>
          {series.filter((s):s is SignalCategoricalSeriesV1=>s.representation==="intervals").map(s=><div className="print-legend" key={s.channel_id}>{Object.entries(s.labels).map(([code,label])=><span key={code} style={{borderLeft:`10px solid ${SLEEP_STAGE_COLORS[label]||SLEEP_STAGE_COLORS.Unknown}`,paddingLeft:4,marginRight:12}}>{label}</span>)}</div>)}</div>;
      })}
    </div>}
    </>
  );
}
